/**
 * Metro Rankings — every US metro sized by its restaurant market.
 *
 * The counterpart to the Trade Area Map. That report asks how we are doing
 * where we already are; this one asks **where we should be**, so its list is
 * every CBSA rather than the ones we ship into. 829 of the 935 have no gallons
 * at all — those rows are the deliverable, not a gap, and the table shows them
 * with the same weight as the rest.
 *
 * Clicking a metro opens the same review the map gives a trade area — who
 * serves it, what sells there, what the agents have flagged — plus the thing
 * only this report can answer: how far the market sits from each plant.
 *
 * Nothing is computed here. Every figure, ranks and distances included, arrives
 * in the payload where `derive` owns it.
 */

import { useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

const NONE = '—'

function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function whole(value: number | null): string {
  return value === null ? NONE : Math.round(value).toLocaleString('en-US')
}

function money(value: number | null): string {
  return value === null ? NONE : `$${Math.round(value).toLocaleString('en-US')}`
}

function pct(value: number | null, places = 1): string {
  return value === null ? NONE : `${value.toFixed(places)}%`
}

function miles(value: number | null): string {
  return value === null ? NONE : `${Math.round(value).toLocaleString('en-US')} mi`
}

/** The standing badge, on the same vocabulary the trade area map uses. */
function standingClass(standing: string | null): string {
  if (standing === null) return 'mrk-badge'
  if (standing.includes('far behind')) return 'mrk-badge mrk-bad'
  if (standing.includes('behind')) return 'mrk-badge mrk-warn'
  return 'mrk-badge mrk-good'
}

function Kpi({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <article className="mrk-kpi">
      <p className="mrk-kpi-label">{label}</p>
      <p className="mrk-kpi-value cell-number">{value}</p>
      {detail !== undefined && <p className="mrk-kpi-detail">{detail}</p>}
    </article>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mrk-section">
      <h3 className="mrk-section-title">{title}</h3>
      {children}
    </section>
  )
}

export function MetroRanking({ result, values }: CustomReportProps) {
  const headline = dataset(result, 'headline')[0]
  const metros = dataset(result, 'metros')
  const distributors = dataset(result, 'metro_distributors')
  const products = dataset(result, 'metro_products')
  const findings = dataset(result, 'metro_findings')
  const asOf = dataset(result, 'as_of')

  const [selected, setSelected] = useState<number | null>(null)

  const rankBy = typeof values['rank_by'] === 'string' ? values['rank_by'] : 'food_service_estab'
  const rankLabel =
    rankBy === 'population'
      ? 'population'
      : rankBy === 'headroom_gal'
        ? 'headroom'
        : rankBy === 'gallons_ttm'
          ? 'gallons'
          : 'restaurants'

  if (headline === undefined) {
    return <Empty label="This run carried no metros" />
  }

  const forMetro = (rows: readonly Keyed[]) =>
    selected === null ? [] : rows.filter((row) => num(row['cbsa_code']) === selected)

  const chosen = metros.find((row) => num(row['cbsa_code']) === selected)
  const metroDistributors = forMetro(distributors)
  const metroProducts = forMetro(products)
  const metroFindings = forMetro(findings)

  return (
    <div className="bespoke-report mrk-page">
      <section className="mrk-kpis">
        <Kpi
          label="Metros in scope"
          value={whole(num(headline['metros']))}
          detail={`${whole(num(headline['shown']))} shown, ranked by ${rankLabel}`}
        />
        <Kpi
          label="We ship there"
          value={whole(num(headline['served']))}
          detail={`${whole(num(headline['unserved']))} with no gallons at all`}
        />
        <Kpi
          label="Restaurants reached"
          value={pct(num(headline['restaurants_pct']))}
          detail={`${whole(num(headline['restaurants_served']))} of ${whole(num(headline['restaurants']))}`}
        />
        <Kpi
          label="Gallons TTM"
          value={whole(num(headline['gallons_ttm']))}
          detail="Distributor street business only"
        />
        <Kpi
          label="Headroom"
          value={whole(num(headline['headroom_gal']))}
          detail="To each metro's own region median"
        />
      </section>

      <Card
        title="Metros, ranked"
        subtitle="Every US metro and micro area, not only the ones we ship into — a metro with no gallons is the point of the list, not a missing row. Click one to open it."
      >
        <div className="mrk-scroll">
          <table className="mrk-table">
            <thead>
              <tr>
                <th scope="col" className="cell-number">
                  #
                </th>
                <th scope="col">Metro</th>
                <th scope="col">Class</th>
                <th scope="col" className="cell-number">
                  Population
                </th>
                <th scope="col" className="cell-number">
                  Restaurants
                </th>
                <th scope="col" className="cell-number">
                  Distributors
                </th>
                <th scope="col" className="cell-number">
                  Gallons TTM
                </th>
                <th scope="col" className="cell-number">
                  Gal / 1k
                </th>
                <th scope="col" className="cell-number">
                  vs region
                </th>
                <th scope="col" className="cell-number">
                  Headroom
                </th>
                <th scope="col" className="cell-number">
                  Tempe
                </th>
                <th scope="col" className="cell-number">
                  Haines City
                </th>
              </tr>
            </thead>
            <tbody>
              {metros.map((row) => {
                const code = num(row['cbsa_code'])
                const gallons = num(row['gallons_ttm'])
                const isOpen = code !== null && code === selected
                return (
                  <tr
                    key={String(code ?? row['cbsa_title'])}
                    className={isOpen ? 'mrk-row is-selected' : 'mrk-row'}
                  >
                    <td className="cell-number mrk-dim">{count(row['rank'])}</td>
                    <td>
                      <button
                        type="button"
                        className="mrk-link"
                        aria-pressed={isOpen}
                        onClick={() => {
                          setSelected(isOpen ? null : code)
                        }}
                      >
                        {text(row['cbsa_title']) ?? NONE}
                      </button>
                      {gallons !== null && gallons <= 0 && (
                        <span className="mrk-tag">no volume</span>
                      )}
                    </td>
                    <td className="mrk-dim">{text(row['cbsa_class']) ?? NONE}</td>
                    <td className="cell-number">{whole(num(row['population']))}</td>
                    <td className="cell-number">{whole(num(row['food_service_estab']))}</td>
                    <td className="cell-number">{count(row['distributors_present'])}</td>
                    <td className="cell-number">{whole(gallons)}</td>
                    <td className="cell-number">{whole(num(row['gal_per_1k_estab']))}</td>
                    <td className="cell-number">{pct(num(row['pct_of_region_norm']), 0)}</td>
                    <td className="cell-number">{whole(num(row['headroom_gal']))}</td>
                    <td className="cell-number mrk-dim">{miles(num(row['miles_to_az']))}</td>
                    <td className="cell-number mrk-dim">{miles(num(row['miles_to_fl']))}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {chosen !== undefined && (
        <Card
          title={text(chosen['cbsa_title']) ?? 'Selected metro'}
          subtitle="The same review the trade area map gives an area, for one metro — plus how far it sits from each plant."
        >
          <button
            type="button"
            className="mrk-clear"
            onClick={() => {
              setSelected(null)
            }}
          >
            Close
          </button>

          <dl className="mrk-facts">
            <div>
              <dt>Population</dt>
              <dd>{whole(num(chosen['population']))}</dd>
            </div>
            <div>
              <dt>Restaurants</dt>
              <dd>{whole(num(chosen['food_service_estab']))}</dd>
            </div>
            <div>
              <dt>Median household income</dt>
              <dd>{money(num(chosen['median_household_income']))}</dd>
            </div>
            <div>
              <dt>Region</dt>
              <dd>{text(chosen['region']) ?? NONE}</dd>
            </div>
            <div>
              <dt>Standing</dt>
              <dd>
                <span className={standingClass(text(chosen['standing']))}>
                  {text(chosen['standing']) ?? 'no benchmark'}
                </span>
              </dd>
            </div>
            <div>
              <dt>Trade area</dt>
              <dd>{text(chosen['trade_area_title']) ?? 'not in one'}</dd>
            </div>
            <div>
              <dt>Miles to Tempe, AZ</dt>
              <dd>{miles(num(chosen['miles_to_az']))}</dd>
            </div>
            <div>
              <dt>Miles to Haines City, FL</dt>
              <dd>{miles(num(chosen['miles_to_fl']))}</dd>
            </div>
            <div>
              <dt>Nearest plant</dt>
              <dd>
                {text(chosen['nearest_plant']) ?? NONE} · {miles(num(chosen['miles_to_nearest']))}
              </dd>
            </div>
          </dl>
          <p className="mrk-note">
            Straight-line miles from the metro&rsquo;s population centroid. It answers which plant
            a market is nearer to — it is not road mileage, transit time or freight cost.
          </p>

          <Section title={`Distributors serving it (${String(metroDistributors.length)})`}>
            {metroDistributors.length === 0 ? (
              <Empty label="No distributor ships into this metro. That is the finding, not missing data." />
            ) : (
              <table className="mrk-table">
                <thead>
                  <tr>
                    <th scope="col">Distributor</th>
                    <th scope="col">Ship-to</th>
                    <th scope="col">City</th>
                    <th scope="col">Channel</th>
                    <th scope="col" className="cell-number">
                      Lines
                    </th>
                    <th scope="col" className="cell-number">
                      Orders TTM
                    </th>
                    <th scope="col" className="cell-number">
                      Gallons TTM
                    </th>
                    <th scope="col">Last order</th>
                  </tr>
                </thead>
                <tbody>
                  {metroDistributors.map((row) => (
                    <tr key={text(row['ship_to_number']) ?? ''}>
                      <td>{text(row['distributor_group']) ?? NONE}</td>
                      <td className="mrk-dim">{text(row['ship_to_number']) ?? NONE}</td>
                      <td>
                        {text(row['city']) ?? NONE}
                        {text(row['state']) === null ? '' : `, ${text(row['state']) ?? ''}`}
                      </td>
                      <td>{text(row['distribution']) ?? NONE}</td>
                      <td className="cell-number">{count(row['lines_carried'])}</td>
                      <td className="cell-number">{count(row['orders_ttm'])}</td>
                      <td className="cell-number">{whole(num(row['gallons_ttm']))}</td>
                      <td>{text(row['last_order_date']) ?? NONE}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          <Section title="What sells there">
            {metroProducts.length === 0 ? (
              <Empty label="Nothing has shipped into this metro in the trailing year." />
            ) : (
              <table className="mrk-table">
                <thead>
                  <tr>
                    <th scope="col">Product line</th>
                    <th scope="col" className="cell-number">
                      Gallons TTM
                    </th>
                    <th scope="col" className="cell-number">
                      Ship-tos
                    </th>
                    <th scope="col" className="cell-number">
                      Share of metro
                    </th>
                    <th scope="col">Last order</th>
                  </tr>
                </thead>
                <tbody>
                  {metroProducts.map((row) => (
                    <tr key={text(row['product_line']) ?? ''}>
                      <td>{text(row['product_line']) ?? NONE}</td>
                      <td className="cell-number">{whole(num(row['gallons_ttm']))}</td>
                      <td className="cell-number">{count(row['ship_tos'])}</td>
                      <td className="cell-number">{pct(num(row['pct_of_metro_mix']))}</td>
                      <td>{text(row['last_order_date']) ?? NONE}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {metroFindings.length > 0 && (
            <Section title={`Open findings here (${String(metroFindings.length)})`}>
              <table className="mrk-table">
                <thead>
                  <tr>
                    <th scope="col">Agent</th>
                    <th scope="col">Severity</th>
                    <th scope="col">End user</th>
                    <th scope="col">Finding</th>
                    <th scope="col" className="cell-number">
                      Gallons at risk
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {metroFindings.map((row, index) => (
                    <tr key={`${text(row['ship_to_number']) ?? ''}-${String(index)}`}>
                      <td>{text(row['agent_code']) ?? NONE}</td>
                      <td>{text(row['severity']) ?? NONE}</td>
                      <td>{text(row['end_user']) ?? NONE}</td>
                      <td className="mrk-wrap">{text(row['title']) ?? NONE}</td>
                      <td className="cell-number">{whole(num(row['gallons_at_risk']))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </Card>
      )}

      <Card title="What each figure is as of">
        <table className="mrk-table">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">As of</th>
              <th scope="col">What it means</th>
            </tr>
          </thead>
          <tbody>
            {asOf.map((row) => (
              <tr key={text(row['source']) ?? ''}>
                <td className="mrk-field">{text(row['source'])}</td>
                <td className="mrk-dim">{text(row['as_of'])}</td>
                <td className="mrk-wrap mrk-dim">{text(row['note'])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
